(() => {
    const {enhancePurchaseOfferPreview, resetPurchaseOfferScroll} = require('./modules/purchase-offer-preview.js');
    const {recordPurchaseStage} = require('./modules/purchase-funnel.js');
    const FOCUSABLE_SELECTOR = [
        'a[href]',
        'button:not([disabled])',
        'summary',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const returnFocusByDialog = new WeakMap();
    const previousOverflowByDialog = new WeakMap();

    function focusableElements(dialog) {
        return Array.from(dialog?.querySelectorAll?.(FOCUSABLE_SELECTOR) ?? [])
            .filter(element => !element.hidden && element.getAttribute?.('aria-hidden') !== 'true')
            .filter(element => !element.getClientRects || element.getClientRects().length > 0);
    }

    function openPremiumReadingDialog(dialog, trigger = null) {
        if (!dialog) {
            return false;
        }

        if (trigger?.focus) {
            returnFocusByDialog.set(dialog, trigger);
        }

        let openedNatively = false;
        if (typeof dialog.showModal === 'function') {
            try {
                if (!dialog.open) {
                    dialog.showModal();
                }
                openedNatively = true;
            } catch (error) {
                openedNatively = false;
            }
        }

        if (!openedNatively) {
            dialog.setAttribute?.('open', '');
            dialog.setAttribute?.('aria-modal', 'true');
            dialog.setAttribute?.('data-am-premium-reading-dialog-fallback', '');
        }

        const rootStyle = dialog.ownerDocument?.documentElement?.style;
        if (rootStyle && !previousOverflowByDialog.has(dialog)) {
            previousOverflowByDialog.set(dialog, rootStyle.overflow);
            rootStyle.overflow = 'hidden';
        }

        const focusTarget = dialog.querySelector?.('[data-am-premium-reading-focus]')
            ?? dialog.querySelector?.('[data-am-premium-reading-checkout]')
            ?? dialog.querySelector?.('.am-birthchart-premium-dialog__action')
            ?? dialog.querySelector?.('[data-am-premium-reading-close]')
            ?? dialog;
        focusTarget.focus?.();
        resetPurchaseOfferScroll(dialog);

        return true;
    }

    function closePremiumReadingDialog(dialog, restoreFocus = true) {
        if (!dialog) {
            return;
        }

        if (typeof dialog.close === 'function' && dialog.open) {
            try {
                dialog.close();
            } catch (error) {
                // Attribute cleanup below also supports lightweight and legacy DOMs.
            }
        }

        dialog.removeAttribute?.('open');
        dialog.removeAttribute?.('aria-modal');
        dialog.removeAttribute?.('data-am-premium-reading-dialog-fallback');

        if (previousOverflowByDialog.has(dialog)) {
            dialog.ownerDocument.documentElement.style.overflow = previousOverflowByDialog.get(dialog);
            previousOverflowByDialog.delete(dialog);
        }

        const returnTarget = returnFocusByDialog.get(dialog);
        returnFocusByDialog.delete(dialog);
        if (restoreFocus) {
            returnTarget?.focus?.();
        }
    }

    function keepDialogFocus(dialog, event) {
        if (event.key !== 'Tab') {
            return false;
        }

        const elements = focusableElements(dialog);
        if (elements.length === 0) {
            event.preventDefault?.();
            dialog.focus?.();
            return true;
        }

        const first = elements[0];
        const last = elements[elements.length - 1];
        const activeElement = dialog.ownerDocument?.activeElement;

        if (event.shiftKey && (activeElement === first || !dialog.contains?.(activeElement))) {
            event.preventDefault?.();
            last.focus?.();
            return true;
        }

        if (!event.shiftKey && (activeElement === last || !dialog.contains?.(activeElement))) {
            event.preventDefault?.();
            first.focus?.();
            return true;
        }

        return false;
    }

    function checkoutErrorMessage(status) {
        if (status === 401) {
            return 'Sua sessão expirou. Entre novamente para continuar.';
        }

        if (status === 409) {
            return 'Seu acesso Premium já está ativo. Recarregue a página para abrir a leitura.';
        }

        return 'Tente novamente em instantes ou acesse a página de assinatura.';
    }

    function enhancePremiumReadingGate(
        documentObject,
        windowObject,
        fetchImplementation = windowObject?.fetch?.bind(windowObject)
    ) {
        const dialog = documentObject?.querySelector?.('[data-am-premium-reading-dialog]');
        if (!dialog) {
            return null;
        }

        enhancePurchaseOfferPreview(dialog, windowObject);

        const triggers = Array.from(
            documentObject.querySelectorAll?.('[data-am-premium-reading-trigger]') ?? []
        );
        const closeControls = Array.from(
            dialog.querySelectorAll?.('[data-am-premium-reading-close]') ?? []
        );
        const checkoutButton = dialog.querySelector?.('[data-am-premium-reading-checkout]');
        const errorNotice = dialog.querySelector?.('[data-am-premium-reading-error]');
        const statusNotice = dialog.querySelector?.('[data-am-premium-reading-status]');
        const errorMessage = dialog.querySelector?.('[data-am-premium-reading-error-message]');
        let checkoutBusy = false;

        function clearError() {
            if (errorNotice) {
                errorNotice.hidden = true;
            }
            if (errorMessage) {
                errorMessage.textContent = '';
            }
        }

        function setCheckoutBusy(busy) {
            checkoutBusy = busy;
            if (statusNotice) {
                statusNotice.hidden = !busy;
            }
            if (!checkoutButton) {
                return;
            }
            checkoutButton.disabled = busy;
            checkoutButton.setAttribute?.('aria-busy', busy ? 'true' : 'false');
        }

        function showError(status) {
            if (errorMessage) {
                errorMessage.textContent = checkoutErrorMessage(status);
            }
            if (errorNotice) {
                errorNotice.hidden = false;
                errorNotice.focus?.();
            }
        }

        async function startCheckout() {
            if (checkoutBusy || typeof fetchImplementation !== 'function') {
                if (typeof fetchImplementation !== 'function') {
                    showError();
                }
                return false;
            }

            clearError();
            setCheckoutBusy(true);
            recordPurchaseStage(windowObject, 'birthchart_houses', 'cta_clicked');

            try {
                const response = await fetchImplementation('/api/subscription/checkout', {
                    method: 'POST',
                    referrerPolicy: 'no-referrer',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        variant: 'original',
                        abTestVariant: 0,
                        source: 'birthchart_houses',
                    }),
                });

                if (!response.ok) {
                    const failure = new Error('premium-reading-checkout-failed');
                    failure.status = response.status;
                    throw failure;
                }

                const data = await response.json();
                if (!data || typeof data.checkout_url !== 'string') {
                    throw new Error('premium-reading-checkout-invalid');
                }

                recordPurchaseStage(windowObject, 'birthchart_houses', 'checkout_redirected');
                windowObject.location.assign(data.checkout_url);
                return true;
            } catch (error) {
                recordPurchaseStage(windowObject, 'birthchart_houses', 'checkout_client_failed');
                showError(error?.status);
                setCheckoutBusy(false);
                return false;
            }
        }

        for (const trigger of triggers) {
            trigger.addEventListener?.('click', () => openPremiumReadingDialog(dialog, trigger));
        }

        for (const control of closeControls) {
            control.addEventListener?.('click', () => closePremiumReadingDialog(dialog));
        }

        dialog.addEventListener?.('click', event => {
            if (event.target === dialog) {
                closePremiumReadingDialog(dialog);
            }
        });
        dialog.addEventListener?.('cancel', event => {
            event.preventDefault?.();
            closePremiumReadingDialog(dialog);
        });
        dialog.addEventListener?.('keydown', event => {
            if (keepDialogFocus(dialog, event) || event.key !== 'Escape') {
                return;
            }
            event.preventDefault?.();
            closePremiumReadingDialog(dialog);
        });
        checkoutButton?.addEventListener?.('click', startCheckout);
        windowObject?.addEventListener?.('pageshow', () => setCheckoutBusy(false));

        if (dialog.hasAttribute?.('data-am-premium-reading-auto-open')) {
            openPremiumReadingDialog(dialog);
        }

        return {
            close: () => closePremiumReadingDialog(dialog),
            dialog,
            open: trigger => openPremiumReadingDialog(dialog, trigger),
            startCheckout,
        };
    }

    function initPremiumReadingGate(documentObject = document, windowObject = window) {
        return enhancePremiumReadingGate(documentObject, windowObject);
    }

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener(
                'DOMContentLoaded',
                () => initPremiumReadingGate(),
                {once: true}
            );
        } else {
            initPremiumReadingGate();
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            checkoutErrorMessage,
            closePremiumReadingDialog,
            enhancePremiumReadingGate,
            initPremiumReadingGate,
            keepDialogFocus,
            openPremiumReadingDialog,
        };
    }
})();
